import { useState, useCallback } from 'react'
import { useEditorStore } from '../store/editor.store'

interface BulkSeoEditorProps {
  onClose: () => void
}

const TITLE_LIMIT = 60
const DESC_LIMIT = 160

function charColor(val: string, limit: number) {
  const n = val.length
  if (n === 0) return '#9ca3af'
  if (n <= limit * 0.8) return '#10b981'
  if (n <= limit) return '#f59e0b'
  return '#ef4444'
}

export function BulkSeoEditor({ onClose }: BulkSeoEditorProps) {
  const pages = useEditorStore(s => s.pages)
  const setActivePage = useEditorStore(s => s.setActivePage)
  const updatePage = useEditorStore(s => s.updatePage)
  const activePageId = useEditorStore(s => s.activePageId)

  const [edits, setEdits] = useState<Record<string, { seoTitle: string; seoDesc: string }>>(() => {
    const map: Record<string, { seoTitle: string; seoDesc: string }> = {}
    for (const p of pages) map[p.id] = { seoTitle: p.seoTitle ?? '', seoDesc: p.seoDesc ?? '' }
    return map
  })
  const [saved, setSaved] = useState<Record<string, boolean>>({})

  const commit = useCallback((pageId: string) => {
    const e = edits[pageId]
    if (!e) return
    const originalActiveId = activePageId
    setActivePage(pageId)
    updatePage({ seoTitle: e.seoTitle, seoDesc: e.seoDesc })
    if (originalActiveId !== pageId) setActivePage(originalActiveId)
    setSaved(prev => ({ ...prev, [pageId]: true }))
    setTimeout(() => setSaved(prev => { const n = { ...prev }; delete n[pageId]; return n }), 1500)
  }, [edits, activePageId, setActivePage, updatePage])

  const tdStyle: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #f3f4f6' }
  const inpStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '900px', maxWidth: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>Массовое редактирование SEO</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>{pages.length} страниц — редактируйте title и description сразу для всех</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#999', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ ...tdStyle, fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', width: '130px' }}>Страница</th>
                <th style={{ ...tdStyle, fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  SEO Title <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>(до {TITLE_LIMIT} символов)</span>
                </th>
                <th style={{ ...tdStyle, fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Meta Description <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>(до {DESC_LIMIT} символов)</span>
                </th>
                <th style={{ ...tdStyle, fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', width: '90px' }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(page => {
                const e = edits[page.id] ?? { seoTitle: '', seoDesc: '' }
                const titleLen = e.seoTitle.length
                const descLen = e.seoDesc.length
                const isDirty = e.seoTitle !== (page.seoTitle ?? '') || e.seoDesc !== (page.seoDesc ?? '')
                return (
                  <tr key={page.id} style={{ background: activePageId === page.id ? '#f0f1ff' : '#fff' }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#111', marginBottom: '2px' }}>
                        {page.title}
                      </div>
                      <div style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>
                        {page.slug || '/'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={{ ...inpStyle, borderColor: titleLen > TITLE_LIMIT ? '#fca5a5' : '#e5e7eb' }}
                        value={e.seoTitle}
                        placeholder={page.title}
                        onChange={ev => setEdits(prev => ({ ...prev, [page.id]: { ...prev[page.id]!, seoTitle: ev.target.value } }))}
                        onBlur={() => isDirty && commit(page.id)}
                      />
                      <div style={{ marginTop: '3px', fontSize: '10px', color: charColor(e.seoTitle, TITLE_LIMIT), textAlign: 'right' }}>
                        {titleLen}/{TITLE_LIMIT}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <textarea
                        style={{ ...inpStyle, minHeight: '52px', borderColor: descLen > DESC_LIMIT ? '#fca5a5' : '#e5e7eb' }}
                        value={e.seoDesc}
                        placeholder="Описание для поисковиков (до 160 символов)"
                        onChange={ev => setEdits(prev => ({ ...prev, [page.id]: { ...prev[page.id]!, seoDesc: ev.target.value } }))}
                        onBlur={() => isDirty && commit(page.id)}
                      />
                      <div style={{ marginTop: '3px', fontSize: '10px', color: charColor(e.seoDesc, DESC_LIMIT), textAlign: 'right' }}>
                        {descLen}/{DESC_LIMIT}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {saved[page.id] ? (
                        <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 700 }}>✓ Сохранено</span>
                      ) : isDirty ? (
                        <button
                          onClick={() => commit(page.id)}
                          style={{ padding: '4px 10px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Сохранить
                        </button>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '16px 28px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
            Изменения сохраняются автоматически при переходе между полями
          </p>
          <button onClick={onClose} style={{ padding: '8px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}
